import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import Skeleton from '@mui/material/Skeleton'
import IconButton from '@mui/material/IconButton'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { adminGetRace, adminCreateRace, adminUpdateRace } from '../../api/admin'
import { STATUS_VALUES } from '../../constants'
import ImageUploader from './ImageUploader'
import { GUIDE_SECTIONS, DEFAULT_GUIDE_SECTIONS } from '../RaceLayout/TabPages/DetailsComponents/RaceGuide/guideSections'
import {
  SectionCard,
  Txt,
  SwitchRow,
  SelectRow,
  ColorField,
  StringList,
  MapEditor,
  TravelParkingEditor,
  CheckpointEditor,
  DropBagEditor,
  AccommodationEditor,
  LocationsEditor,
  WelcomeEditor,
  TrackingInfoEditor,
  OtherRaceInfoEditor,
  PenaltyEditor,
  BibCollectionEditor,
  ScheduleEditor,
  GearKeysEditor,
  KeyValueTable
} from './fields'

const EMPTY_RACE = {
  title: '',
  slug: '',
  subtitle: '',
  description: '',
  type: 'trail race',
  status: 'later',
  published: true,
  registrationsClosed: false,
  regCloseHoursBeforeStart: 12,
  logo: '',
  color: '',
  image: '',
  distances: [],
  elevGain: '',
  rank: '',
  date: [],
  tentativeDate: '',
  myRaceResultsId: '',
  perks: [],
  startTimes: {},
  locationNote: '',
  mosqueLocation: '',
  bibCollection: { preRace: '', raceDay: '' },
  prizes: [],
  gpx: [''],
  locationGpsUrl: '',
  locationWazeUrl: '',
  emergencyContacts: [],
  googleMaps: '',
  waze: '',
  finalEventVideo: '',
  sponsors: '',
  ads: [],
  rules: [],
  penalties: [],
  otherRaceInfo: [],
  trackingInfo: [],
  welcome: {},
  accommodation: { chapters: [] },
  locations: [],
  travelAndParking: null,
  mandatoryGear: {},
  recommendedGear: {},
  raceDistances: [],
  showParticipants: true,
  showResults: false,
  showLive: false,
  showRegistration: false,
  showMedia: true,
  showWelcome: false,
  showDetails: true,
  showPolicies: true,
  showVolunteers: false,
  showContact: true,
  showLinks: true,
  showImageSliderInMedia: false,
  showPasswordForMedia: false,
  showBriefSplashes: false,
  guidePdf: '',
  guideImage: '',
  guideSections: { ...DEFAULT_GUIDE_SECTIONS },
  policiesKey: 'default',
  linkToMedia: '',
  showFeedbackForm: true,
  linkToFeedbackFrom: '',
  mediaPassword: '',
  mediaEventId: '',
  mediaOrgId: ''
}

const BASE_TABS = [
  'General',
  'Media',
  'Welcome',
  'Details',
  'Policies',
  'Registration',
  'Participants',
  'Live',
  'Results',
  'Volunteers',
  'Contact',
  'Links'
]

const TAB_META = {
  General: { target: 'Site-wide, race header, home page and race cards', note: 'Title, slug, dates, status, imagery and accent colour.' },
  Media: { target: 'Race page → Media tab', note: 'Race videos, photo library access and the feedback form.' },
  Welcome: { target: 'Race page → Welcome tab', note: 'Shown before the race guide when the Welcome tab is enabled.' },
  Details: { target: 'Race page → Details tab', note: 'Runner guide accordions, in the exact order they render, plus per-category content.' },
  Policies: { target: 'Race page → Policies tab', note: 'Selects which policy set is displayed.' },
  Registration: { target: 'Race page → Registration tab', note: 'Registration window, start times and per-category extras.' },
  Participants: { target: 'Race page → Participants tab', note: 'Participant list embed (uses the MyRaceResults ID set in General).' },
  Live: { target: 'Race page → Live tab', note: 'Live tracking embed (uses the MyRaceResults ID set in General).' },
  Results: { target: 'Race page → Results tab', note: 'Results embed (uses the MyRaceResults ID set in General).' },
  Volunteers: { target: 'Race page → Volunteers tab', note: 'Volunteer call-out content is fixed; this only shows or hides the tab.' },
  Contact: { target: 'Race page → Contact tab', note: 'Emergency contacts and navigation links.' },
  Links: { target: 'Race page → Links tab', note: 'GPX files and external navigation links.' }
}

function slugify (str) {
  return String(str || '').toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function toDateInput (value) {
  if (!value) return ''
  if (value instanceof Date) return value.toISOString().slice(0, 10)
  if (typeof value === 'string') {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? value : d.toISOString().slice(0, 10)
  }
  return ''
}

const BLANK_DISTANCE = {
  id: '',
  name: '',
  distance: '',
  color: '',
  ascent: '',
  descent: '',
  netDistance: '',
  url: '',
  gpx: '',
  brief: '',
  mapImage: '',
  schedule: { startTime: '', endTime: '', startLocationName: '', finishLocationName: '' },
  checkpoints: [],
  mandatoryGear: [],
  recommendedGear: [],
  prizes: {},
  travelAndParking: [],
  checkpointInfo: [],
  dropBagInfo: null
}

export default function RaceForm () {
  const { slug } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const isEdit = !!slug
  const [activeTab, setActiveTab] = useState(0)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(!!location.state?.saved)
  const [busSchedule, setBusSchedule] = useState(null)
  const [busScheduleJson, setBusScheduleJson] = useState('')
  const originalBusSchedule = useRef(null)

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue
  } = useForm({ defaultValues: EMPTY_RACE })

  const title = watch('title')
  const mandatoryGearKeys = Object.keys(watch('mandatoryGear') || {})
  const recommendedGearKeys = Object.keys(watch('recommendedGear') || {})

  const dateArray = useFieldArray({ control, name: 'date' })
  const distanceArray = useFieldArray({ control, name: 'raceDistances' })

  useEffect(() => {
    if (!isEdit) {
      reset(EMPTY_RACE)
      setLoading(false)
      return
    }

    let active = true
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const race = await adminGetRace(slug)
        if (!race) {
          if (active) setError('Race not found')
          return
        }
        if (active) {
          const values = {
            ...EMPTY_RACE,
            ...race,
            guideSections: { ...DEFAULT_GUIDE_SECTIONS, ...(race.guideSections || {}) },
            date: Array.isArray(race.date) ? race.date.map(toDateInput) : [],
            tentativeDate: race.tentativeDate === undefined ? '' : toDateInput(race.tentativeDate)
          }
          originalBusSchedule.current = race.busSchedule || null
          setBusSchedule(race.busSchedule || null)
          reset(values)
        }
      } catch (err) {
        if (active) setError(err.message)
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => { active = false }
  }, [isEdit, slug, reset])

  const buildPayload = (values) => {
    const payload = {
      ...values,
      busSchedule
    }
    return payload
  }

  const onSubmit = async (values) => {
    setSaving(true)
    setError(null)
    try {
      const payload = buildPayload(values)
      if (isEdit) {
        await adminUpdateRace(slug, payload)
        setSaved(true)
      } else {
        const created = await adminCreateRace(payload)
        navigate(`/admin/dashboard/races/${created.slug}/edit`, { replace: true, state: { saved: true } })
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleImportBusSchedule = (raw) => {
    try {
      const parsed = JSON.parse(raw)
      setBusSchedule(parsed)
      setError(null)
    } catch {
      setError('Invalid bus schedule JSON')
    }
  }

  const gearKeys = useMemo(() => Array.from(new Set([...mandatoryGearKeys, ...recommendedGearKeys])), [mandatoryGearKeys, recommendedGearKeys])

  const guideSectionToggle = (section) => (
    <SwitchRow
      key={`${section.id}-toggle`}
      label={`Show "${section.label}" accordion`}
      value={watch(`guideSections.${section.id}`)}
      onChange={v => setValue(`guideSections.${section.id}`, v)}
    />
  )

  const guideSectionFields = (section) => {
    switch (section.id) {
      case 'courseBrief':
        return (
          <SwitchRow label='Show brief splash image' value={watch('showBriefSplashes')} onChange={v => setValue('showBriefSplashes', v)} />
        )
      case 'mandatoryGear':
        return (
          <Controller
            control={control}
            name='mandatoryGear'
            render={({ field }) => <MapEditor label='Mandatory gear (key → text)' value={field.value} onChange={field.onChange} />}
          />
        )
      case 'recommendedGear':
        return (
          <Controller
            control={control}
            name='recommendedGear'
            render={({ field }) => <MapEditor label='Recommended gear (key → text)' value={field.value} onChange={field.onChange} />}
          />
        )
      case 'travelAndParking':
        return (
          <>
            <Controller
              control={control}
              name='travelAndParking'
              render={({ field }) => <TravelParkingEditor label='Common travel & parking' value={field.value} onChange={field.onChange} />}
            />
            <BusScheduleSection
              busSchedule={busSchedule}
              busScheduleJson={busScheduleJson}
              setBusScheduleJson={setBusScheduleJson}
              onImport={handleImportBusSchedule}
              onRestore={() => setBusSchedule(originalBusSchedule.current)}
              onClear={() => setBusSchedule(null)}
            />
          </>
        )
      case 'raceInfo':
        return (
          <Controller
            control={control}
            name='otherRaceInfo'
            render={({ field }) => <OtherRaceInfoEditor value={field.value} onChange={field.onChange} />}
          />
        )
      case 'rulesAndPenalties':
        return (
          <>
            <Controller
              control={control}
              name='rules'
              render={({ field }) => <StringList label='Rules' value={field.value} onChange={field.onChange} />}
            />
            <Controller
              control={control}
              name='penalties'
              render={({ field }) => <PenaltyEditor value={field.value} onChange={field.onChange} />}
            />
          </>
        )
      case 'trackingAndContacts':
        return (
          <Controller
            control={control}
            name='trackingInfo'
            render={({ field }) => <TrackingInfoEditor value={field.value} onChange={field.onChange} />}
          />
        )
      case 'travelGuide':
        return (
          <Controller
            control={control}
            name='accommodation'
            render={({ field }) => <AccommodationEditor value={field.value} onChange={field.onChange} />}
          />
        )
      case 'finishPrizes':
        return (
          <Controller
            control={control}
            name='prizes'
            render={({ field }) => (
              <MapEditor
                label='Prizes intro (title & description) — per-category prizes are in Distances below'
                value={Array.isArray(field.value) ? {} : field.value}
                onChange={field.onChange}
              />
            )}
          />
        )
      case 'sponsors':
        return (
          <>
            <Txt label='Sponsors (image URL)' value={watch('sponsors')} onChange={v => setValue('sponsors', v)} />
            <Controller
              control={control}
              name='ads'
              render={({ field }) => <StringList label='Ads' value={field.value} onChange={field.onChange} />}
            />
          </>
        )
      default:
        return null
    }
  }

  const GUIDE_SECTION_NOTES = {
    updates: 'Content is currently fixed on the page. Use the toggle to include or exclude this accordion.',
    schedule: 'Per-category start/end times and locations are edited in the Distances section below.',
    courseMap: 'The map is generated from the GPX set per category in the Distances section below.',
    checkpointsGuide: 'Checkpoints are edited per category in the Distances section below.',
    dropBagInfo: 'Drop bag info is edited per category in the Distances section below.',
    finishPrizes: 'Prizes are edited per category in the Distances section below.',
    aidStationInfo: 'Content is currently fixed on the page. Use the toggle to include or exclude this accordion.',
    raceVenueInfo: 'Content is currently fixed on the page. Use the toggle to include or exclude this accordion.'
  }

  if (loading) {
    return <Skeleton variant='rectangular' height={600} />
  }

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant='h5' component='h1'>
          {isEdit ? `Edit Race — ${slug}` : 'New Race'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => navigate('/admin/dashboard/races')}>Cancel</Button>
          <Button type='submit' variant='contained' disabled={saving}>
            {saving ? 'Saving…' : 'Save Race'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}

      <Snackbar
        open={saved}
        autoHideDuration={3000}
        onClose={() => setSaved(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity='success' variant='filled' onClose={() => setSaved(false)}>
          Race saved successfully
        </Alert>
      </Snackbar>

      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        variant='scrollable'
        scrollButtons='auto'
        sx={{ mb: 1 }}
      >
        {BASE_TABS.map(t => <Tab key={t} label={t} />)}
      </Tabs>

      <Paper variant='outlined' sx={{ p: 1.5, mb: 2 }}>
        <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
          {TAB_META[BASE_TABS[activeTab]]?.target}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {TAB_META[BASE_TABS[activeTab]]?.note}
        </Typography>
      </Paper>

      {activeTab === 0 && (
        <Stack spacing={2}>
          <SectionCard title='Race identity'>
            <ImageUploader label='Race main image' hint='Recommended resolution: 1400 x 700' value={watch('image')} onChange={v => setValue('image', v)} />
            <Txt label='Title' value={watch('title')} onChange={v => setValue('title', v)} />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{ flexGrow: 1 }}>
                <Txt label='Slug' value={watch('slug')} onChange={v => setValue('slug', slugify(v))} />
              </Box>
              <Button
                size='small'
                startIcon={<AutoAwesomeIcon />}
                sx={{ mt: 1 }}
                onClick={() => {
                  const firstDate = watch('date')?.find(d => d)
                  const year = firstDate ? new Date(firstDate).getFullYear() : new Date().getFullYear()
                  setValue('slug', slugify(`${title}-${year}`))
                }}
              >
                Auto-slugify
              </Button>
            </Box>
            <Txt label='Subtitle' value={watch('subtitle')} onChange={v => setValue('subtitle', v)} />
            <Txt label='Type' value={watch('type')} onChange={v => setValue('type', v)} />
            <SelectRow label='Status' value={watch('status')} onChange={v => setValue('status', v)} options={STATUS_VALUES} />
            <SwitchRow label='Published' value={watch('published')} onChange={v => setValue('published', v)} />
          </SectionCard>

          <SectionCard title='Dates'>
            <Box>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>Race dates</Typography>
              {dateArray.fields.map((field, i) => (
                <Box key={field.id} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    type='date'
                    size='small'
                    fullWidth
                    value={watch(`date.${i}`) || ''}
                    onChange={e => setValue(`date.${i}`, e.target.value)}
                  />
                  <IconButton size='small' disabled={dateArray.fields.length <= 1} onClick={() => dateArray.remove(i)}><DeleteIcon fontSize='small' /></IconButton>
                </Box>
              ))}
              <Button size='small' startIcon={<AddIcon />} onClick={() => dateArray.append('')}>Add date</Button>
            </Box>
            <Txt label='Tentative date' type='date' value={watch('tentativeDate')} onChange={v => setValue('tentativeDate', v)} />
          </SectionCard>

          <SectionCard title='Branding & facts'>
            <ImageUploader label='Race logo' hint='Upload a transparent PNG logo (Cloudinary)' value={watch('logo')} onChange={v => setValue('logo', v)} />
            <ColorField label='Accent color (home page & event page text)' value={watch('color')} onChange={v => setValue('color', v)} />
            <Controller
              control={control}
              name='distances'
              render={({ field }) => <StringList label='Distances (shown on cards and lists)' value={field.value} onChange={field.onChange} />}
            />
            <Txt label='Elevation gain' value={watch('elevGain')} onChange={v => setValue('elevGain', v)} />
            <Controller
              control={control}
              name='rank'
              render={({ field }) => (
                Array.isArray(field.value)
                  ? <StringList label='Rank' value={field.value} onChange={field.onChange} />
                  : <Txt label='Rank' value={field.value} onChange={field.onChange} />
              )}
            />
          </SectionCard>

          <SectionCard title='Shared embeds'>
            <Txt
              label='MyRaceResults ID (used by Registration, Participants, Live and Results)'
              value={watch('myRaceResultsId')}
              onChange={v => setValue('myRaceResultsId', v)}
            />
          </SectionCard>
        </Stack>
      )}

      {activeTab === 1 && (
        <Stack spacing={2}>
          <SectionCard title='Media tab'>
            <SwitchRow label='Show media tab' value={watch('showMedia')} onChange={v => setValue('showMedia', v)} />
            <Controller
              control={control}
              name='finalEventVideo'
              render={({ field }) => (
                Array.isArray(field.value)
                  ? <StringList label='Race videos' value={field.value} onChange={field.onChange} />
                  : <Txt label='Race video' value={field.value} onChange={field.onChange} />
              )}
            />
            <SwitchRow label='Show image slider in media' value={watch('showImageSliderInMedia')} onChange={v => setValue('showImageSliderInMedia', v)} />
          </SectionCard>

          <SectionCard title='Photo library access'>
            <SwitchRow label='Require password for media' value={watch('showPasswordForMedia')} onChange={v => setValue('showPasswordForMedia', v)} />
            <Txt label='Media password' value={watch('mediaPassword')} onChange={v => setValue('mediaPassword', v)} />
            <Txt label='Photo distribution event ID' value={watch('mediaEventId')} onChange={v => setValue('mediaEventId', v)} />
            <Txt label='Photo distribution organization ID' value={watch('mediaOrgId')} onChange={v => setValue('mediaOrgId', v)} />
            <Txt label='Link to media' value={watch('linkToMedia')} onChange={v => setValue('linkToMedia', v)} />
          </SectionCard>

          <SectionCard title='Feedback form'>
            <SwitchRow label='Show feedback form' value={watch('showFeedbackForm')} onChange={v => setValue('showFeedbackForm', v)} />
            <Txt label='Link to feedback form' value={watch('linkToFeedbackFrom')} onChange={v => setValue('linkToFeedbackFrom', v)} />
          </SectionCard>
        </Stack>
      )}

      {activeTab === 2 && (
        <SectionCard title='Welcome tab'>
          <SwitchRow label='Show welcome tab' value={watch('showWelcome')} onChange={v => setValue('showWelcome', v)} />
          <Controller
            control={control}
            name='welcome'
            render={({ field }) => <WelcomeEditor value={field.value} onChange={field.onChange} />}
          />
        </SectionCard>
      )}

      {activeTab === 3 && (
        <Stack spacing={2}>
          <SectionCard title='Details tab'>
            <SwitchRow label='Show details tab' value={watch('showDetails')} onChange={v => setValue('showDetails', v)} />
            <Txt
              label='Description (shown below the race guide)'
              value={watch('description')}
              multiline
              minRows={3}
              onChange={v => setValue('description', v)}
            />
          </SectionCard>

          <SectionCard title='Race guide download'>
            <Typography variant='caption' color='text.secondary'>
              Shown at the top of the Details tab as a clickable thumbnail. Leave the PDF URL empty to hide it.
              Translations (e.g. Arabic) are managed on the document's `translations` field.
            </Typography>
            <Txt label='Guide PDF URL' value={watch('guidePdf')} onChange={v => setValue('guidePdf', v)} />
            <Txt label='Guide thumbnail image URL' value={watch('guideImage')} onChange={v => setValue('guideImage', v)} />
          </SectionCard>

          <Paper variant='outlined' sx={{ p: 1.5 }}>
            <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>Race guide accordions — in render order</Typography>
            <Typography variant='caption' color='text.secondary'>
              Each entry below is one accordion on the Details page, in the exact order runners see it. Toggle the switch to include or exclude it.
            </Typography>
          </Paper>

          {GUIDE_SECTIONS.map((section, index) => (
            <SectionCard key={section.id} title={`${index + 1}. ${section.label}`}>
              {guideSectionToggle(section)}
              {GUIDE_SECTION_NOTES[section.id] && (
                <Typography variant='caption' color='text.secondary'>{GUIDE_SECTION_NOTES[section.id]}</Typography>
              )}
              {guideSectionFields(section)}
            </SectionCard>
          ))}

          <SectionCard title='Locations (used by schedule & drop bag links)'>
            <Controller
              control={control}
              name='locations'
              render={({ field }) => <LocationsEditor value={field.value} onChange={field.onChange} />}
            />
          </SectionCard>

          <SectionCard title='Distances (per-category guide content)'>
            {distanceArray.fields.map((field, i) => {
              const prefix = `raceDistances.${i}`
              const distance = watch(prefix)
              return (
                <Paper key={field.id} variant='outlined' sx={{ p: 2, mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography
                      variant='subtitle1' sx={{
                      fontWeight: 600
                    }}
                    >
                      {distance?.name || `Distance ${i + 1}`}
                    </Typography>
                    <IconButton size='small' color='error' onClick={() => distanceArray.remove(i)}>
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </Box>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 1 }}>
                      <Txt label='ID' type='number' value={watch(`${prefix}.id`)} onChange={v => setValue(`${prefix}.id`, v)} />
                      <Txt label='Name' value={watch(`${prefix}.name`)} onChange={v => setValue(`${prefix}.name`, v)} />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 1 }}>
                      <Txt label='Distance (km)' type='number' value={watch(`${prefix}.distance`)} onChange={v => setValue(`${prefix}.distance`, v)} />
                      <Txt label='Color' value={watch(`${prefix}.color`)} onChange={v => setValue(`${prefix}.color`, v)} />
                      <Txt label='Ascent' type='number' value={watch(`${prefix}.ascent`)} onChange={v => setValue(`${prefix}.ascent`, v)} />
                      <Txt label='Descent' type='number' value={watch(`${prefix}.descent`)} onChange={v => setValue(`${prefix}.descent`, v)} />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1 }}>
                      <Txt label='Net distance' type='number' value={watch(`${prefix}.netDistance`)} onChange={v => setValue(`${prefix}.netDistance`, v)} />
                      <Txt label='URL' value={watch(`${prefix}.url`)} onChange={v => setValue(`${prefix}.url`, v)} />
                      <Txt label='GPX' value={watch(`${prefix}.gpx`)} onChange={v => setValue(`${prefix}.gpx`, v)} />
                    </Box>
                    <Txt label='Brief (Course Brief accordion)' value={watch(`${prefix}.brief`)} multiline minRows={4} onChange={v => setValue(`${prefix}.brief`, v)} />
                    <Txt label='Map image (Course Map accordion)' value={watch(`${prefix}.mapImage`)} onChange={v => setValue(`${prefix}.mapImage`, v)} />
                    <Controller
                      control={control}
                      name={`${prefix}.schedule`}
                      render={({ field }) => <ScheduleEditor value={field.value} onChange={field.onChange} />}
                    />
                    <Controller
                      control={control}
                      name={`${prefix}.checkpoints`}
                      render={({ field }) => <CheckpointEditor value={field.value} onChange={field.onChange} />}
                    />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                      <Controller
                        control={control}
                        name={`${prefix}.mandatoryGear`}
                        render={({ field }) => <GearKeysEditor label='Mandatory gear keys' value={field.value} onChange={field.onChange} availableKeys={gearKeys} />}
                      />
                      <Controller
                        control={control}
                        name={`${prefix}.recommendedGear`}
                        render={({ field }) => <GearKeysEditor label='Recommended gear keys' value={field.value} onChange={field.onChange} availableKeys={gearKeys} />}
                      />
                    </Box>
                    <Controller
                      control={control}
                      name={`${prefix}.prizes`}
                      render={({ field }) => <MapEditor label='Finish prizes' value={field.value} onChange={field.onChange} />}
                    />
                    <Controller
                      control={control}
                      name={`${prefix}.travelAndParking`}
                      render={({ field }) => <TravelParkingEditor label='Local shuttles & parking' value={field.value} onChange={field.onChange} />}
                    />
                    <Controller
                      control={control}
                      name={`${prefix}.checkpointInfo`}
                      render={({ field }) => <OtherRaceInfoEditor value={field.value} onChange={field.onChange} />}
                    />
                    <Controller
                      control={control}
                      name={`${prefix}.dropBagInfo`}
                      render={({ field }) => (
                        <DropBagEditor
                          value={field.value}
                          onChange={field.onChange}
                          locations={watch('locations') || []}
                          distances={watch('raceDistances') || []}
                        />
                      )}
                    />
                  </Stack>
                </Paper>
              )
            })}
            <Button
              variant='outlined'
              startIcon={<AddIcon />}
              onClick={() => distanceArray.append({ ...BLANK_DISTANCE, id: distanceArray.fields.length + 1 })}
            >
              Add distance
            </Button>
          </SectionCard>
        </Stack>
      )}

      {activeTab === 4 && (
        <SectionCard title='Policies tab'>
          <SwitchRow label='Show policies tab' value={watch('showPolicies')} onChange={v => setValue('showPolicies', v)} />
          <Txt label='Policies key' value={watch('policiesKey')} onChange={v => setValue('policiesKey', v)} />
        </SectionCard>
      )}

      {activeTab === 5 && (
        <Stack spacing={2}>
          <SectionCard title='Registration tab'>
            <SwitchRow label='Show registration tab' value={watch('showRegistration')} onChange={v => setValue('showRegistration', v)} />
            <SwitchRow label='Registrations closed' value={watch('registrationsClosed')} onChange={v => setValue('registrationsClosed', v)} />
            <Txt label='Reg close hours before start' type='number' value={watch('regCloseHoursBeforeStart')} onChange={v => setValue('regCloseHoursBeforeStart', v)} />
            <Controller
              control={control}
              name='startTimes'
              render={({ field }) => (
                field.value !== null && typeof field.value === 'object' && !Array.isArray(field.value)
                  ? <MapEditor label='Start times (used to compute registration cutoff)' value={field.value} onChange={field.onChange} />
                  : <StringList label='Start times' value={field.value} onChange={field.onChange} />
              )}
            />
            <Controller
              control={control}
              name='bibCollection'
              render={({ field }) => <BibCollectionEditor value={field.value} onChange={field.onChange} />}
            />
            <Controller
              control={control}
              name='perks'
              render={({ field }) => <StringList label='Perks' value={field.value} onChange={field.onChange} />}
            />
          </SectionCard>
        </Stack>
      )}

      {activeTab === 6 && (
        <SectionCard title='Participants tab'>
          <SwitchRow label='Show participants tab' value={watch('showParticipants')} onChange={v => setValue('showParticipants', v)} />
          <Typography variant='body2' color='text.secondary'>
            The participant list is embedded from MyRaceResults using the ID set in the General tab.
          </Typography>
        </SectionCard>
      )}

      {activeTab === 7 && (
        <SectionCard title='Live tab'>
          <SwitchRow label='Show live tab' value={watch('showLive')} onChange={v => setValue('showLive', v)} />
          <Typography variant='body2' color='text.secondary'>
            Live tracking is embedded from MyRaceResults using the ID set in the General tab.
          </Typography>
        </SectionCard>
      )}

      {activeTab === 8 && (
        <SectionCard title='Results tab'>
          <SwitchRow label='Show results tab' value={watch('showResults')} onChange={v => setValue('showResults', v)} />
          <Typography variant='body2' color='text.secondary'>
            Results are embedded from MyRaceResults using the ID set in the General tab.
          </Typography>
        </SectionCard>
      )}

      {activeTab === 9 && (
        <SectionCard title='Volunteers tab'>
          <SwitchRow label='Show volunteers tab' value={watch('showVolunteers')} onChange={v => setValue('showVolunteers', v)} />
          <Typography variant='body2' color='text.secondary'>
            Volunteer content is fixed on the page; this only shows or hides the tab.
          </Typography>
        </SectionCard>
      )}

      {activeTab === 10 && (
        <Stack spacing={2}>
          <SectionCard title='Contact tab'>
            <SwitchRow label='Show contact tab' value={watch('showContact')} onChange={v => setValue('showContact', v)} />
            <Controller
              control={control}
              name='emergencyContacts'
              render={({ field }) => <StringList label='Emergency contacts' value={field.value} onChange={field.onChange} />}
            />
          </SectionCard>

          <SectionCard title='Location & navigation'>
            <Txt label='Google Maps' value={watch('googleMaps')} onChange={v => setValue('googleMaps', v)} />
            <Txt label='Waze' value={watch('waze')} onChange={v => setValue('waze', v)} />
            <Txt label='Mosque location' value={watch('mosqueLocation')} onChange={v => setValue('mosqueLocation', v)} />
            <Txt label='Location note' value={watch('locationNote')} multiline minRows={2} onChange={v => setValue('locationNote', v)} />
            <Controller
              control={control}
              name='locationForWeather'
              render={({ field }) => (
                Array.isArray(field.value)
                  ? <StringList label='Location for weather' value={field.value} onChange={field.onChange} />
                  : <Txt label='Location for weather' value={field.value} onChange={field.onChange} />
              )}
            />
          </SectionCard>
        </Stack>
      )}

      {activeTab === 11 && (
        <Stack spacing={2}>
          <SectionCard title='Links tab'>
            <SwitchRow label='Show links tab' value={watch('showLinks')} onChange={v => setValue('showLinks', v)} />
            <Controller
              control={control}
              name='gpx'
              render={({ field }) => <StringList label='GPX files' value={field.value} onChange={field.onChange} />}
            />
            <Txt label='Location GPS URL' value={watch('locationGpsUrl')} onChange={v => setValue('locationGpsUrl', v)} />
            <Txt label='Location Waze URL' value={watch('locationWazeUrl')} onChange={v => setValue('locationWazeUrl', v)} />
          </SectionCard>
        </Stack>
      )}
    </Box>
  )
}

function BusScheduleSection ({ busSchedule, busScheduleJson, setBusScheduleJson, onImport, onRestore, onClear }) {
  return (
    <Box>
      <Typography variant='subtitle2' sx={{ mb: 1 }}>Bus schedule (Local Shuttles And Parking accordion)</Typography>
      <Typography variant='body2' sx={{ mb: 2, color: 'text.secondary' }}>
        The bus schedule is a large structured object. Edit it by pasting updated JSON, restoring the last saved
        version, or clearing it. Structured table editing is out of scope.
      </Typography>
      {busSchedule ? (
        <Box sx={{ mb: 2 }}>
          <KeyValueTable
            label='Schedule preview' data={{
            'Schedule entries': Array.isArray(busSchedule.schedule) ? busSchedule.schedule.map(s => `${s.distance}km (${s.trips?.length} trips)`).join(', ') : '—',
            'General notes': Array.isArray(busSchedule.generalNotes) ? busSchedule.generalNotes.length : '—'
          }}
          />
        </Box>
      ) : (
        <Typography variant='body2' sx={{ mb: 2 }}>No bus schedule set for this race.</Typography>
      )}
      <TextField
        label='Paste bus schedule JSON'
        multiline
        minRows={6}
        size='small'
        fullWidth
        sx={{ mb: 1 }}
        value={busScheduleJson}
        onChange={e => setBusScheduleJson(e.target.value)}
      />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button size='small' variant='contained' onClick={() => onImport(busScheduleJson)}>
          Import JSON
        </Button>
        <Button size='small' onClick={onRestore}>
          Restore last saved
        </Button>
        <Button size='small' color='error' onClick={onClear}>
          Clear
        </Button>
      </Box>
    </Box>
  )
}
